import { useTranslation } from 'react-i18next';

import type { FilterConfig, SelectOption } from './types';
import type { ReactNode } from 'react';

import { AsyncAutoComplete } from '@/components/AsyncAutoComplete';
import { DatePicker } from '@/components/DatePicker';
import { FieldClearButton } from '@/components/FieldClearButton';
import { Input } from '@/components/Input';
import { MonthPicker } from '@/components/MonthPicker';
import { MultiSelect } from '@/components/MultiSelect';
import { Select } from '@/components/Select';

import { cn } from '@/lib/utils';

import { toText } from '@/helpers';

type FilterFieldProps = { filter: FilterConfig };

const BOOLEAN_VALUE_BY_OPTION: Record<string, boolean> = {
  yes: true,
  no: false,
};

const textValue = (value: unknown) => (value ? toText(value) : '');

const clearHandler = (filter: FilterConfig) =>
  filter.hideClearButton ? undefined : filter.onClear;

const FieldCell = (props: { isTopAligned?: boolean; children: ReactNode }) => {
  const { isTopAligned = false, children } = props;

  return (
    <div
      className={cn(
        'flex w-full flex-1 grow gap-1',
        isTopAligned ? 'items-start' : 'items-end',
      )}
    >
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
};

const SelectFilterField = (props: FilterFieldProps) => {
  const { filter } = props;
  const { t } = useTranslation();

  return (
    <FieldCell>
      <Select
        value={textValue(filter.value)}
        onValueChange={(value) => filter.onChange(value || null)}
        options={(filter.options || []) as SelectOption[]}
        label={filter.label}
        placeholder={filter.placeholder ?? t('Select a value')}
        disabled={filter.disabled}
        searchable={filter.searchable}
        onClear={clearHandler(filter)}
      />
    </FieldCell>
  );
};

const MultiSelectFilterField = (props: FilterFieldProps) => {
  const { filter } = props;
  const { t } = useTranslation();
  const values = ((filter.value as (string | number)[]) || []).map(String);
  const options = ((filter.options || []) as SelectOption[]).map(
    ({ id, name }) => ({ id, name }),
  );

  return (
    <FieldCell isTopAligned>
      <MultiSelect
        label={filter.label}
        placeholder={filter.placeholder ?? t('Select a value')}
        value={values}
        options={options}
        onValueChange={filter.onChange}
        disabled={filter.disabled}
        searchable={filter.searchable}
        onClear={clearHandler(filter)}
      />
    </FieldCell>
  );
};

const BooleanFilterField = (props: FilterFieldProps) => {
  const { filter } = props;
  const { t } = useTranslation();
  const options = [
    { id: 'yes', name: t('Yes') },
    { id: 'no', name: t('No') },
  ];
  const optionValue =
    Object.keys(BOOLEAN_VALUE_BY_OPTION).find(
      (option) => BOOLEAN_VALUE_BY_OPTION[option] === filter.value,
    ) ?? '';

  return (
    <FieldCell>
      <Select
        value={optionValue}
        onValueChange={(value) =>
          filter.onChange(BOOLEAN_VALUE_BY_OPTION[value] ?? null)
        }
        options={options}
        label={filter.label}
        placeholder={filter.placeholder ?? t('Select a value')}
        disabled={filter.disabled}
        onClear={clearHandler(filter)}
      />
    </FieldCell>
  );
};

const DateFilterField = (props: FilterFieldProps) => {
  const { filter } = props;
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();

  return (
    <FieldCell>
      <DatePicker
        label={filter.label}
        placeholder={filter.placeholder ?? t('Select a date')}
        value={textValue(filter.value)}
        onChange={(value) => filter.onChange(value || null)}
        disabled={filter.disabled}
        fromYear={currentYear - 10}
        toYear={currentYear + 10}
        onClear={clearHandler(filter)}
      />
    </FieldCell>
  );
};

const TextFilterField = (props: FilterFieldProps) => {
  const { filter } = props;
  const value = textValue(filter.value);
  const isClearable = !!value && !filter.hideClearButton && !filter.disabled;

  return (
    <Input
      label={filter.label}
      placeholder={filter.placeholder}
      value={value}
      onChange={(event) => filter.onChange(event.target.value || null)}
      disabled={filter.disabled}
      endAdornment={
        isClearable && <FieldClearButton onClear={filter.onClear} />
      }
    />
  );
};

const MonthFilterField = (props: FilterFieldProps) => {
  const { filter } = props;

  return (
    <FieldCell>
      <MonthPicker
        label={filter.label}
        placeholder={filter.placeholder}
        value={textValue(filter.value)}
        onChange={(value) => filter.onChange(value || null)}
        disabled={filter.disabled}
        onClear={clearHandler(filter)}
      />
    </FieldCell>
  );
};

const AutocompleteFilterField = (props: FilterFieldProps) => {
  const { filter } = props;

  return (
    <FieldCell>
      <AsyncAutoComplete
        fetchUrl={filter.fetchUrl || ''}
        queryKey={filter.queryKey || filter.name}
        selectedValue={filter.value || null}
        onSelectedValueChange={filter.onChange}
        getOptionLabel={
          filter.getOptionLabel || ((option: unknown) => String(option))
        }
        getOptionValue={
          filter.getOptionValue || ((option: unknown) => String(option))
        }
        transformResponse={
          filter.transformResponse ||
          ((response: unknown) => response as unknown[])
        }
        label={filter.label}
        placeholder={filter.placeholder}
        disabled={filter.disabled}
        onClear={clearHandler(filter)}
      />
    </FieldCell>
  );
};

const FILTER_FIELDS: Record<
  FilterConfig['type'],
  (props: FilterFieldProps) => ReactNode
> = {
  select: SelectFilterField,
  multiselect: MultiSelectFilterField,
  boolean: BooleanFilterField,
  date: DateFilterField,
  text: TextFilterField,
  month: MonthFilterField,
  autocomplete: AutocompleteFilterField,
};

export const ListFilterField = (props: FilterFieldProps) => {
  const { filter } = props;
  const Field = FILTER_FIELDS[filter.type];

  return <Field filter={filter} />;
};
